import React, { useState } from "react";
import { Megaphone, Siren, Check, Archive, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { comunicados as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * COMUNICADOS Y ALERTAS.
 *
 * Una alerta de emergencia es un comunicado con otro tipo: mismo recorrido,
 * misma segmentación, mismo historial. Se distinguen por color y por icono,
 * que es lo único que de verdad cambia para quien lo lee.
 */

const FORM_VACIO = { title: "", body: "", kind: "aviso", audience: "todos", polygons: "", houses: "", expiresAt: "" };

const COLOR = {
  emergencia: "var(--danger)",
  cierre: "var(--warning)",
  mantenimiento: "var(--warning)",
  aviso: "var(--gold)",
  reunion: "var(--gold)",
  recordatorio: "var(--text-tertiary)",
  evento: "var(--gold)",
};

const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "medium", timeStyle: "short" });

export default function Comunicados({ rol }) {
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.announcements || [];
  const esAdmin = rol === "admin";

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await api.crear({
        ...form,
        polygons: form.polygons ? form.polygons.split(",").map((s) => s.trim()).filter(Boolean) : [],
        houses: form.houses ? form.houses.split(",").map((s) => s.trim()).filter(Boolean) : [],
        expiresAt: form.expiresAt || null,
      });
      setAbierta(false);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo publicar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Comunicados"
        subtitulo={
          datos?.sinLeer ? `Tiene ${datos.sinLeer} sin leer.` : "Lo que la administración le dice a la comunidad."
        }
        accion={esAdmin ? "Publicar" : null}
        alAccionar={() => setAbierta(true)}
        icono={Megaphone}
      />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : lista.length === 0 ? (
        <Vacio>No hay comunicados.</Vacio>
      ) : (
        <div style={{ display: "grid", gap: "var(--space-4)" }}>
          {lista.map((a) => (
            <div
              key={a._id}
              style={{
                ...est.tarjeta,
                borderLeft: `4px solid ${COLOR[a.kind] || "var(--gold)"}`,
                opacity: a.leido ? 0.75 : 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                {a.kind === "emergencia" && <Siren size={18} style={{ color: "var(--danger)" }} />}
                <h3 style={est.tarjetaTitulo}>{a.title}</h3>
              </div>
              <div style={est.tarjetaDato}>{fecha(a.createdAt)}{a.createdBy?.name ? ` · ${a.createdBy.name}` : ""}</div>
              <div style={{ ...est.tarjetaDato, display: "block", whiteSpace: "pre-wrap" }}>{a.body}</div>

              <div style={est.tarjetaAcciones}>
                {!a.leido && (
                  <Button size="sm" variant="outline" leftIcon={Check}
                    onClick={() => api.marcarLeido(a._id).then(recargar)}>
                    Marcar como leído
                  </Button>
                )}
                {esAdmin && a.isActive && (
                  <Button size="sm" variant="ghost" leftIcon={Archive}
                    onClick={() => api.archivar(a._id).then(recargar)}>
                    Archivar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Publicar un comunicado"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>Publicar</Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Título" value={form.title} onChange={cambiar("title")} required />
        <Area etiqueta="Mensaje" value={form.body} onChange={cambiar("body")} required />
        <div style={est.fila}>
          <Seleccion
            etiqueta="Tipo"
            value={form.kind}
            onChange={cambiar("kind")}
            opciones={[
              { valor: "aviso", etiqueta: "Aviso" },
              { valor: "mantenimiento", etiqueta: "Mantenimiento" },
              { valor: "emergencia", etiqueta: "🚨 Emergencia" },
              { valor: "cierre", etiqueta: "Cierre" },
              { valor: "reunion", etiqueta: "Reunión" },
              { valor: "recordatorio", etiqueta: "Recordatorio" },
              { valor: "evento", etiqueta: "Evento" },
            ]}
          />
          <Seleccion
            etiqueta="Para quién"
            value={form.audience}
            onChange={cambiar("audience")}
            opciones={[
              { valor: "todos", etiqueta: "Toda la residencial" },
              { valor: "residentes", etiqueta: "Sólo residentes" },
              { valor: "guardias", etiqueta: "Sólo guardias" },
              { valor: "administracion", etiqueta: "Sólo administración" },
              { valor: "poligono", etiqueta: "Polígonos concretos" },
              { valor: "casas", etiqueta: "Casas concretas" },
            ]}
          />
        </div>

        {form.audience === "poligono" && (
          <Entrada etiqueta="Polígonos (separados por coma)" value={form.polygons} onChange={cambiar("polygons")} placeholder="A, B" />
        )}
        {form.audience === "casas" && (
          <Entrada etiqueta="Casas (separadas por coma)" value={form.houses} onChange={cambiar("houses")} placeholder="12, 14" />
        )}

        <Entrada etiqueta="Se deja de mostrar el (opcional)" type="date" value={form.expiresAt} onChange={cambiar("expiresAt")} />
      </Ventana>
    </div>
  );
}
